import React, { useMemo } from 'react';
import PanelCardHeader from './PanelCardHeader';
import './PanelCard.css';

interface PanelCardProps {
  title: string;

  width?: number;
  height?: number;

  onRemove?: () => void;

  // kept for compatibility, but no longer used by manual inputs
  onWidthChange?: (newWidth: number) => void;
  onHeightChange?: (newHeight: number) => void;

  // now means "show size info bar"
  showSizeControls?: boolean;

  // explicit remove visibility control
  showRemove?: boolean;

  rightHeaderSlot?: React.ReactNode;

  children?: React.ReactNode;

  className?: string;
  style?: React.CSSProperties;

  /**
   * For RGL usage:
   * You usually pass { width: '100%', height: '100%' } here
   * to make the card fill its grid cell.
   */
  draggablePropsStyle?: React.CSSProperties;
}

const PanelCard: React.FC<PanelCardProps> = ({
  title,
  width = 300,
  height = 200,
  onRemove,
  showSizeControls = true,
  showRemove = true,
  rightHeaderSlot,
  children,
  className,
  style,
  draggablePropsStyle,
}) => {
  const normalizedRightSlot = useMemo(() => {
    if (!rightHeaderSlot) return null;
    return <div className="panel-card-right-slot">{rightHeaderSlot}</div>;
  }, [rightHeaderSlot]);

  return (
    <div
      className={`panel-card ${className ?? ''}`}
      style={{
        width,
        height,
        ...draggablePropsStyle,
        ...style,
      }}
    >
      <PanelCardHeader
        title={title}
        onRemove={onRemove}
        showRemove={showRemove}
        rightSlot={normalizedRightSlot}
      />

      <div className="panel-card-body">{children}</div>

      {showSizeControls && (
        <div className="panel-card-size panel-card-size-readonly">
          <span className="panel-card-size-label">W:</span>
          <span className="panel-card-size-value">{Math.round(width)}px</span>

          <span className="panel-card-size-sep">·</span>

          <span className="panel-card-size-label">H:</span>
          <span className="panel-card-size-value">{Math.round(height)}px</span>

          <span className="panel-card-size-hint">Resize the panel to change</span>
        </div>
      )}
    </div>
  );
};

export default PanelCard;
